import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Send, Bot, User, Trash2, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function EditorChat({ onInsertText }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/api/ai-editor/chat/history`, {
        headers: getHeaders(), withCredentials: true
      });
      setMessages(res.data.messages || []);
    } catch {}
    setHistoryLoaded(true);
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");

    // Optimistic update
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/api/ai-editor/chat`, {
        message: userMsg,
      }, {
        headers: getHeaders(), withCredentials: true
      });
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (err) {
      toast.error("Chat failed");
      setMessages((prev) => prev.slice(0, -1));
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/api/ai-editor/chat/history`, {
        headers: getHeaders(), withCredentials: true
      });
      setMessages([]);
      toast.success("Chat history cleared");
    } catch {}
  };

  if (!historyLoaded) {
    return <div className="text-sm text-slate-400 text-center py-8">Loading chat history...</div>;
  }

  return (
    <div className="flex flex-col h-[500px] border border-slate-200 rounded">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <Bot className="w-4 h-4 text-cyan-500" />
          Editor AI Assistant
        </div>
        {messages.length > 0 && (
          <button onClick={handleClear} className="p-1 text-slate-400 hover:text-red-500">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Bot className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">Ask me to rewrite, correct, or improve your text.</p>
            <p className="text-xs text-slate-300 mt-1">Try: "Make this more persuasive" or "Shorten this paragraph"</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-2 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            {msg.role === "assistant" && (
              <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center shrink-0">
                <Bot className="w-3.5 h-3.5 text-cyan-600" />
              </div>
            )}
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
              msg.role === "user"
                ? "bg-cyan-600 text-white"
                : "bg-slate-100 text-slate-700"
            }`}>
              <pre className="whitespace-pre-wrap font-sans text-sm">{msg.content}</pre>
              {msg.role === "assistant" && onInsertText && (
                <button
                  onClick={() => onInsertText(msg.content)}
                  className="mt-1.5 text-[10px] text-cyan-600 hover:text-cyan-700 font-medium"
                >
                  Insert into Editor
                </button>
              )}
            </div>
            {msg.role === "user" && (
              <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center shrink-0">
                <User className="w-3.5 h-3.5 text-slate-500" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-2">
            <div className="w-7 h-7 bg-cyan-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-3.5 h-3.5 text-cyan-600 animate-spin" />
            </div>
            <div className="bg-slate-100 rounded-lg px-3 py-2 text-sm text-slate-400">
              Thinking...
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-slate-200 p-3">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Editor AI to rewrite or improve text..."
            rows={2}
            className="flex-1 border border-slate-300 rounded px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-400"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading}
            className="bg-cyan-600 hover:bg-cyan-500 rounded-none self-end h-9"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
